/**
 * EntityHubPage — Single-entity "company brain" view.
 *
 * Renders the response shape of get_entity_hub_data into five sections:
 *
 *   1. Header        — entity name + type + key metadata chips + actions
 *   2. Insights      — auto-synthesized paragraph (or "synthesis pending")
 *   3. What we know  — KB entries grouped by category, with edit/link actions
 *   4. Activity      — recent usage_events stats and 5 most recent turns
 *   5. Relationships — 1-hop entity graph (outbound + inbound)
 *
 * Each section degrades gracefully: when the backend returns empty data
 * (e.g., a new entity with no KB entries yet), an empty-state with a
 * "teach Trendy" call-to-action is shown instead of a blank section.
 *
 * URL: /knowledge/entity/:type/:id
 *   e.g. /knowledge/entity/customer/247  →  PMI
 *        /knowledge/entity/vendor/1713   →  Infinity Furniture
 *        /knowledge/entity/project/25712 →  Hilton Signia project
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, RefreshCw, MessageSquare, Plus, Sparkles, Loader2,
  AlertCircle, Clock, User, Tag, Activity, Link2, ChevronDown,
  ChevronRight, Lightbulb, Building2, Truck, Package, Briefcase,
  UserCircle, Folder, ExternalLink,
} from 'lucide-react'
import { useTools } from '../hooks/useTools'

// ─── Entity-type display metadata ───────────────────────────────────────────

// Note: Tailwind's JIT compiler scans source text for literal class names — it
// cannot resolve interpolated strings like `bg-${color}-100`. So we store the
// full class strings inline (iconBg / iconText) rather than a `color` token.
const ENTITY_TYPE_META = {
  customer: {
    label: 'Customer', icon: Building2,
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    iconText: 'text-emerald-600 dark:text-emerald-400',
  },
  vendor: {
    label: 'Vendor', icon: Truck,
    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
    iconText: 'text-orange-600 dark:text-orange-400',
  },
  part: {
    label: 'Part', icon: Package,
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconText: 'text-amber-600 dark:text-amber-400',
  },
  job: {
    label: 'Job', icon: Briefcase,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconText: 'text-blue-600 dark:text-blue-400',
  },
  employee: {
    label: 'Employee', icon: UserCircle,
    iconBg: 'bg-violet-100 dark:bg-violet-900/30',
    iconText: 'text-violet-600 dark:text-violet-400',
  },
  project: {
    label: 'Project', icon: Folder,
    iconBg: 'bg-rose-100 dark:bg-rose-900/30',
    iconText: 'text-rose-600 dark:text-rose-400',
  },
}

// Category badge colors — mirror the KB page for visual consistency
const CATEGORY_BADGE = {
  customer_note: { label: 'Customer Note', color: 'text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' },
  vendor_note: { label: 'Vendor Note', color: 'text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20' },
  part_note: { label: 'Part Note', color: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20' },
  pricing_note: { label: 'Pricing', color: 'text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20' },
  business_rule: { label: 'Business Rule', color: 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' },
  entity_relationship: { label: 'Relationship', color: 'text-gray-700 dark:text-gray-400 bg-gray-50 dark:bg-gray-800' },
}

// ─── Utility ────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 30) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function fmtUSD(n) {
  if (n == null) return null
  try { return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) }
  catch { return String(n) }
}

// ─── Try-it-in-chat (shared with KB page) ──────────────────────────────────

function useTryInChat() {
  const navigate = useNavigate()
  return useCallback((prompt) => {
    sessionStorage.setItem('kb_try_prompt', prompt)
    navigate('/')
  }, [navigate])
}

// ─── Header section ─────────────────────────────────────────────────────────

function HubHeader({ entity, onRefresh, onAskTrendy, onBack, refreshing }) {
  const typeMeta = ENTITY_TYPE_META[entity.type] || ENTITY_TYPE_META.customer
  const TypeIcon = typeMeta.icon
  const md = entity.metadata || {}

  // Build the chips that show at the top of the header — varies by entity type
  const chips = useMemo(() => {
    const out = []
    if (entity.type === 'customer' || entity.type === 'vendor') {
      if (md.cust_id || md.vendor_id) out.push({ label: 'ID', value: md.cust_id || md.vendor_id })
      if (md.terms_code) out.push({ label: 'Terms', value: md.terms_code })
      if (md.credit_hold === true) out.push({ label: 'Credit Hold', value: 'YES', warn: true })
      if (md.credit_hold === false) out.push({ label: 'Credit Hold', value: 'No' })
      if (md.city || md.state) out.push({ label: 'Location', value: [md.city, md.state].filter(Boolean).join(', ') })
      if (md.inactive === true) out.push({ label: 'Status', value: 'Inactive', warn: true })
    } else if (entity.type === 'project') {
      if (md.customer_id) out.push({ label: 'Customer', value: md.customer_id })
      if (md.po_num) out.push({ label: 'PO', value: md.po_num })
      if (md.total_amount) out.push({ label: 'Total', value: fmtUSD(md.total_amount) })
      if (md.open === true) out.push({ label: 'Status', value: 'Open' })
      if (md.open === false) out.push({ label: 'Status', value: 'Closed' })
      if (md.order_held === true) out.push({ label: 'Hold', value: 'YES', warn: true })
    } else if (entity.type === 'job') {
      if (md.part_num) out.push({ label: 'Part', value: md.part_num })
      if (md.prod_qty) out.push({ label: 'Qty', value: md.prod_qty })
      if (md.released === true) out.push({ label: 'Released', value: 'Yes' })
      if (md.closed === true) out.push({ label: 'Status', value: 'Closed' })
      else if (md.complete === true) out.push({ label: 'Status', value: 'Complete' })
    } else if (entity.type === 'part') {
      if (md.uom) out.push({ label: 'UOM', value: md.uom })
      if (md.class_id) out.push({ label: 'Class', value: md.class_id })
      if (md.type_code) out.push({ label: 'Type', value: md.type_code })
      if (md.inactive === true) out.push({ label: 'Status', value: 'Inactive', warn: true })
    } else if (entity.type === 'employee') {
      if (md.department) out.push({ label: 'Dept', value: md.department })
      if (md.email) out.push({ label: 'Email', value: md.email })
    }
    return out
  }, [entity, md])

  return (
    <div className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-surface-dark pb-5 mb-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-4 text-xs">
        <button onClick={onBack} className="text-gray-500 hover:text-amtrend-blue flex items-center gap-1">
          <ArrowLeft size={12} /> Back
        </button>
        <span className="text-gray-400">/</span>
        <Link to="/knowledge" className="text-gray-500 hover:text-amtrend-blue">Knowledge</Link>
        <span className="text-gray-400">/</span>
        <span className="text-gray-500 capitalize">{typeMeta.label}s</span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-700 dark:text-gray-300 truncate">{entity.display_name}</span>
      </div>

      <div className="flex items-start gap-4">
        {/* Entity-type icon */}
        <div className={`p-3 rounded-xl ${typeMeta.iconBg} flex-shrink-0`}>
          <TypeIcon size={24} className={typeMeta.iconText} />
        </div>

        {/* Title + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <span className="uppercase tracking-wider font-semibold">{typeMeta.label}</span>
            <span>·</span>
            <span className="font-mono">{entity.id}</span>
            {!entity.found_in_epicor && (
              <>
                <span>·</span>
                <span className="text-amber-600 dark:text-amber-400">Not found in Epicor</span>
              </>
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
            {entity.display_name}
          </h1>

          {/* Metadata chips */}
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {chips.map((c, i) => (
                <span
                  key={i}
                  className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${
                    c.warn
                      ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <span className="text-gray-500 dark:text-gray-400 font-medium">{c.label}:</span>
                  <span className="font-medium">{c.value}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          <button
            onClick={onAskTrendy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amtrend-blue text-white hover:bg-amtrend-blue/90 transition-colors"
          >
            <MessageSquare size={14} />
            Ask Trendy
          </button>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Section wrapper ────────────────────────────────────────────────────────

function HubSection({ title, count, icon: Icon, children, action }) {
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className="text-gray-400" />}
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {title}
          </h2>
          {count != null && (
            <span className="text-xs text-gray-400 dark:text-gray-500">({count})</span>
          )}
        </div>
        {action}
      </div>
      {children}
    </div>
  )
}

// ─── Insights section ──────────────────────────────────────────────────────

function InsightsSection({ insights, onSynthesize }) {
  if (!insights || !insights.text) {
    return (
      <HubSection title="Insights" icon={Sparkles}>
        <div className="p-4 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 text-center">
          <Sparkles size={20} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            Synthesis pending — Trendy will summarize what we know about this entity once
            enough activity accumulates.
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            (Auto-generated every 6 hours)
          </p>
        </div>
      </HubSection>
    )
  }
  return (
    <HubSection title="Insights" icon={Sparkles}>
      <div className="p-4 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/40 dark:bg-violet-900/10">
        <div className="text-sm leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-line">
          {insights.text}
        </div>
        <div className="mt-3 pt-3 border-t border-violet-200/50 dark:border-violet-800/50 text-xs text-gray-400 dark:text-gray-500 flex items-center gap-2">
          <Sparkles size={12} />
          Auto-synthesized {timeAgo(insights.generated_at)}
          {insights.is_stale && (
            <span className="text-amber-600 dark:text-amber-400 ml-2">· stale, refresh recommended</span>
          )}
        </div>
      </div>
    </HubSection>
  )
}

// ─── Knowledge section ─────────────────────────────────────────────────────

function KnowledgeSection({ knowledge, onAddKnowledge }) {
  const byCategory = knowledge?.by_category || {}
  const cats = Object.keys(byCategory)

  if (cats.length === 0) {
    return (
      <HubSection
        title="What We Know"
        icon={Lightbulb}
        action={
          <button
            onClick={onAddKnowledge}
            className="text-xs text-amtrend-blue hover:underline inline-flex items-center gap-1"
          >
            <Plus size={12} /> Add knowledge
          </button>
        }
      >
        <div className="p-4 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 text-center">
          <Lightbulb size={20} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            No facts stored yet.
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Ask Trendy something like: "Remember that this customer requires FOB Fullerton"
          </p>
        </div>
      </HubSection>
    )
  }

  return (
    <HubSection
      title="What We Know"
      count={knowledge.count}
      icon={Lightbulb}
      action={
        <button
          onClick={onAddKnowledge}
          className="text-xs text-amtrend-blue hover:underline inline-flex items-center gap-1"
        >
          <Plus size={12} /> Add knowledge
        </button>
      }
    >
      <div className="space-y-2">
        {cats.map((cat) => {
          const badge = CATEGORY_BADGE[cat] || { label: cat, color: 'text-gray-700 bg-gray-50' }
          return byCategory[cat].map((entry) => (
            <KBEntryCard key={entry.id} entry={entry} badge={badge} />
          ))
        })}
      </div>
    </HubSection>
  )
}

function KBEntryCard({ entry, badge }) {
  const [expanded, setExpanded] = useState(false)
  const isLong = (entry.value || '').length > 220
  return (
    <div className="p-3 rounded-lg border border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay transition-colors">
      <div className="flex items-start gap-3">
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 mt-0.5 ${badge.color}`}>
          {badge.label}
        </span>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100">{entry.key}</h4>
          <p className={`mt-1 text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line ${!expanded && isLong ? 'line-clamp-3' : ''}`}>
            {entry.value}
          </p>
          {isLong && (
            <button onClick={() => setExpanded(!expanded)} className="mt-1 text-xs text-amtrend-blue hover:underline">
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400 dark:text-gray-500">
            {entry.created_by && <span className="inline-flex items-center gap-1"><User size={10} />{entry.created_by}</span>}
            {entry.updated_at && <span className="inline-flex items-center gap-1"><Clock size={10} />{timeAgo(entry.updated_at)}</span>}
            {entry.source && entry.source !== 'manual' && <span className="inline-flex items-center gap-1"><Tag size={10} />{entry.source}</span>}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Activity section ──────────────────────────────────────────────────────

function ActivitySection({ activity }) {
  const count = activity?.count_30d ?? 0
  if (count === 0) {
    return (
      <HubSection title="Activity (last 30 days)" icon={Activity}>
        <div className="p-4 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 text-center">
          <Activity size={20} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No Trendy interactions in the last 30 days.
          </p>
        </div>
      </HubSection>
    )
  }
  return (
    <HubSection title="Activity (last 30 days)" count={count} icon={Activity}>
      {/* Top agents */}
      {(activity.top_agents || []).length > 0 && (
        <div className="mb-3">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Most-used tools</div>
          <div className="flex flex-wrap gap-1.5">
            {activity.top_agents.map((a, i) => (
              <span key={i} className="text-xs px-2 py-1 rounded-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300">
                <span className="font-mono">{a.agent}</span>
                <span className="ml-1.5 text-gray-400 dark:text-gray-500">{a.count}×</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Recent turns */}
      {(activity.recent_turns || []).length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Recent</div>
          {activity.recent_turns.map((t) => (
            <div key={t.id} className="p-2 rounded-md border border-gray-100 dark:border-gray-800 text-xs flex items-start gap-2">
              <Clock size={10} className="text-gray-400 mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-gray-700 dark:text-gray-300 truncate">{t.user_message}</div>
                <div className="mt-0.5 text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-2">
                  <span>{t.user_id}</span>
                  <span>·</span>
                  <span>{timeAgo(t.timestamp)}</span>
                  <span>·</span>
                  <span className="font-mono">{t.task_category}</span>
                  {t.agent_call_count > 0 && (
                    <>
                      <span>·</span>
                      <span>{t.agent_call_count} tool{t.agent_call_count !== 1 ? 's' : ''}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </HubSection>
  )
}

// ─── Relationships section ─────────────────────────────────────────────────

function RelationshipsSection({ relationships }) {
  const outbound = relationships?.outbound || []
  const inbound = relationships?.inbound || []
  const total = outbound.length + inbound.length
  if (total === 0) {
    return (
      <HubSection title="Relationships" icon={Link2}>
        <div className="p-4 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 text-center">
          <Link2 size={20} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            No relationships recorded yet.
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Ask Trendy: "Link this customer to its parent account" or similar.
          </p>
        </div>
      </HubSection>
    )
  }
  return (
    <HubSection title="Relationships" count={total} icon={Link2}>
      <div className="space-y-3">
        {outbound.length > 0 && (
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Outbound ({outbound.length})</div>
            <div className="flex flex-wrap gap-2">
              {outbound.map((r, i) => (
                <Link
                  key={i}
                  to={`/knowledge/entity/${r.target_type}/${encodeURIComponent(r.target_id)}`}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-700 hover:border-amtrend-blue/40 hover:bg-amtrend-blue/5 text-gray-700 dark:text-gray-300 transition-colors"
                >
                  <span className="text-gray-400 dark:text-gray-500">{r.relationship}</span>
                  <span className="text-gray-300 dark:text-gray-600">→</span>
                  <span className="font-medium">{r.target_type}/{r.target_id}</span>
                  <ExternalLink size={10} className="text-gray-300 dark:text-gray-600" />
                </Link>
              ))}
            </div>
          </div>
        )}
        {inbound.length > 0 && (
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Inbound ({inbound.length})</div>
            <div className="flex flex-wrap gap-2">
              {inbound.map((r, i) => (
                <Link
                  key={i}
                  to={`/knowledge/entity/${r.source_type}/${encodeURIComponent(r.source_id)}`}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-700 hover:border-amtrend-blue/40 hover:bg-amtrend-blue/5 text-gray-700 dark:text-gray-300 transition-colors"
                >
                  <span className="font-medium">{r.source_type}/{r.source_id}</span>
                  <span className="text-gray-300 dark:text-gray-600">→</span>
                  <span className="text-gray-400 dark:text-gray-500">{r.relationship}</span>
                  <ExternalLink size={10} className="text-gray-300 dark:text-gray-600" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </HubSection>
  )
}

// ─── Main page ─────────────────────────────────────────────────────────────

export default function EntityHubPage() {
  // Route is /knowledge/entity/:entityType/:entityId — names must match.
  const { entityType: type, entityId: id } = useParams()
  const navigate = useNavigate()
  const tryInChat = useTryInChat()
  const { execute } = useTools()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    if (!type || !id) {
      setError('Missing entity type or id in URL.')
      return
    }
    setError(null)
    try {
      const res = await execute('get_entity_hub_data', {
        entity_type: type,
        entity_id: id,
      })
      if (res?.result?.error) {
        setError(res.result.error)
        setData(null)
      } else if (res?.result) {
        setData(res.result)
      } else {
        setError('No data returned from hub endpoint')
      }
    } catch (err) {
      console.error('hub fetch failed', err)
      setError(err.message || 'Failed to load hub')
    }
  }, [execute, type, id])

  useEffect(() => {
    setLoading(true)
    fetchData().finally(() => setLoading(false))
  }, [fetchData])

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchData()
    setRefreshing(false)
  }

  const handleAskTrendy = () => {
    const name = data?.entity?.display_name || id
    tryInChat(`Tell me everything we know about ${name} and pull a full briefing.`)
  }

  const handleAddKnowledge = () => {
    const name = data?.entity?.display_name || id
    tryInChat(`I want to add a knowledge entry about ${name}. Help me capture it correctly with entity_type=${type} and entity_id=${id}.`)
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 size={32} className="animate-spin text-amtrend-blue" />
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <button
          onClick={() => navigate('/knowledge')}
          className="text-xs text-gray-500 hover:text-amtrend-blue inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={12} /> Back to Knowledge
        </button>
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="font-medium text-red-700 dark:text-red-400 mb-1">Unable to load entity hub</h2>
            <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="min-h-screen bg-white dark:bg-surface-dark">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <HubHeader
          entity={data.entity}
          onRefresh={handleRefresh}
          onAskTrendy={handleAskTrendy}
          onBack={() => navigate('/knowledge')}
          refreshing={refreshing}
        />

        <InsightsSection insights={data.insights} />
        <KnowledgeSection knowledge={data.knowledge} onAddKnowledge={handleAddKnowledge} />
        <ActivitySection activity={data.activity} />
        <RelationshipsSection relationships={data.relationships} />
      </div>
    </div>
  )
}
